import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  MultiSelect,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Worker, WorkEntry, WorkerRole } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

interface Props {
  isAdmin: boolean;
}

export function AttendanceTab({ isAdmin }: Props) {
  const { t } = useTranslation();
  const [dateStr, setDateStr] = useState<string>(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  });
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [roles, setRoles] = useState<WorkerRole[]>([]);
  const [entries, setEntries] = useState<WorkEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const [workerIds, setWorkerIds] = useState<string[]>([]);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:30");
  const [amount, setAmount] = useState<string | number>("");
  const [note, setNote] = useState("");

  // Derive today's string for maxDate enforcement
  const todayStr = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }, []);

  async function reload() {
    setLoading(true);
    try {
      const [ws, rs, es] = await Promise.all([
        api.listWorkers(true),
        api.listWorkerRoles(),
        api.listWorkEntries({ from: dateStr, to: dateStr }),
      ]);
      setWorkers(ws);
      setRoles(rs);
      setEntries(es);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [dateStr]);

  const workerOptions = workers.map((w) => ({ value: String(w.id), label: w.full_name }));
  const workerById = new Map(workers.map((w) => [w.id, w] as const));
  const roleById = new Map(roles.map((r) => [r.id, r.name] as const));

  async function add() {
    if (workerIds.length === 0) {
      notifications.show({ color: "red", message: t("workers.attendance.pickWorker") });
      return;
    }
    if (!start || !end) {
      notifications.show({ color: "red", message: t("workers.attendance.timeRequired") });
      return;
    }
    const amt = isAdmin && amount !== "" ? Number(amount) : undefined;
    // One entry per selected worker, all with the same date/time/note.
    const results = await Promise.allSettled(
      workerIds.map((id) =>
        api.createWorkEntry({
          worker_id: Number(id),
          work_date: dateStr,
          start_time: start,
          end_time: end,
          note: note.trim() || null,
          ...(amt !== undefined ? { amount: amt } : {}),
        }),
      ),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok > 0) {
      // Keep the selection + time/note so the same group can be added for
      // another day — just change the date and click Add again.
      await reload();
    }
    if (failed === 0) {
      notifications.show({ color: "teal", message: t("workers.attendance.savedN", { count: ok }) });
    } else {
      notifications.show({
        color: ok > 0 ? "yellow" : "red",
        message: t("workers.attendance.savedPartial", { ok, failed }),
      });
    }
  }

  async function remove(id: number) {
    if (!confirm(t("workers.attendance.confirmDelete"))) return;
    try {
      await api.deleteWorkEntry(id);
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  return (
    <Stack>
      <Group gap="sm" wrap="wrap" align="end">
        <TextInput
          type="date"
          label={t("workers.attendance.date")}
          value={dateStr}
          onChange={(e) => setDateStr(e.currentTarget.value)}
          max={todayStr}
          w={180}
        />
      </Group>

      <Paper withBorder p="sm">
        <Group wrap="wrap" align="end" gap="sm">
          <MultiSelect
            label={t("workers.attendance.worker")}
            placeholder={t("workers.attendance.pickWorkers")}
            data={workerOptions}
            value={workerIds}
            onChange={setWorkerIds}
            searchable
            clearable
            w={320}
            maw="100%"
          />
          <TextInput
            type="time"
            label={t("workers.attendance.start")}
            value={start}
            onChange={(e) => setStart(e.currentTarget.value)}
          />
          <TextInput
            type="time"
            label={t("workers.attendance.end")}
            value={end}
            onChange={(e) => setEnd(e.currentTarget.value)}
          />
          {isAdmin && (
            <NumberInput
              label={t("workers.attendance.amountOverride")}
              value={amount}
              onChange={(v) => setAmount(v)}
              min={0}
              decimalScale={2}
              allowNegative={false}
              w={140}
            />
          )}
          <TextInput
            label={t("workers.attendance.note")}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 160 }}
          />
          <Button leftSection={<IconPlus size={14} />} onClick={() => void add()}>
            {t("workers.attendance.add")}
          </Button>
        </Group>
      </Paper>

      <ResponsiveTable
        mobile={{
          rows: entries,
          rowKey: (e) => e.id,
          title: (e) => workerById.get(e.worker_id)?.full_name ?? `#${e.worker_id}`,
          subtitle: (e) => `${e.start_time}–${e.end_time}`,
          fields: [
            {
              label: t("workers.col.role"),
              render: (e) => {
                const w = workerById.get(e.worker_id);
                return w?.role_id ? roleById.get(w.role_id) ?? "—" : "—";
              },
            },
            ...(isAdmin
              ? [{ label: t("workers.attendance.amount"), render: (e: WorkEntry) => (e.amount != null ? e.amount.toFixed(2) : "—") }]
              : []),
            { label: t("workers.attendance.note"), render: (e) => e.note ?? "—" },
          ],
          actions: isAdmin
            ? (e) => (
                <ActionIcon size="lg" color="red" variant="subtle" onClick={() => void remove(e.id)} title={t("common.delete")}>
                  <IconTrash size={16} />
                </ActionIcon>
              )
            : undefined,
          loading: loading && entries.length === 0,
          empty: t("workers.attendance.empty"),
        }}
      >
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("workers.attendance.worker")}</Table.Th>
            <Table.Th>{t("workers.col.role")}</Table.Th>
            <Table.Th>{t("workers.attendance.startEnd")}</Table.Th>
            {isAdmin && <Table.Th>{t("workers.attendance.amount")}</Table.Th>}
            <Table.Th>{t("workers.attendance.note")}</Table.Th>
            {isAdmin && <Table.Th />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {entries.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={isAdmin ? 6 : 4}>
                <Text c="dimmed" size="sm">{loading ? t("common.loading") : t("workers.attendance.empty")}</Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            entries.map((e) => {
              const w = workerById.get(e.worker_id);
              return (
                <Table.Tr key={e.id}>
                  <Table.Td>{w?.full_name ?? `#${e.worker_id}`}</Table.Td>
                  <Table.Td>{w?.role_id ? roleById.get(w.role_id) ?? "—" : "—"}</Table.Td>
                  <Table.Td>{e.start_time}–{e.end_time}</Table.Td>
                  {isAdmin && <Table.Td>{e.amount != null ? e.amount.toFixed(2) : "—"}</Table.Td>}
                  <Table.Td>{e.note ?? "—"}</Table.Td>
                  {isAdmin && (
                    <Table.Td>
                      <Group gap="xs" justify="flex-end">
                        <ActionIcon color="red" variant="subtle" onClick={() => void remove(e.id)} title={t("common.delete")}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })
          )}
        </Table.Tbody>
      </Table>
      </ResponsiveTable>
    </Stack>
  );
}
